import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import logger from '@/util/logger';
import {
    BRAND_ICON_THUMB_WIDTH,
    BRAND_ICON_URL_WIDTH,
    BRAND_LOGO_THUMB_WIDTH,
    BRAND_LOGO_URL_WIDTH,
    type BrandAssetCandidate,
    brandAssetCandidates,
    brandAssetId,
    brandfetchRasterUrl,
    parseSvgAspect,
} from '@rock/brands';
import type { Brand, BrandAssetSlot, BrandfetchCandidate, BrandLogoImage, BrandSource } from '@rock/types';
import {
    Check,
    ChevronRight,
    Globe,
    ImageOff,
    Loader2,
    MoreHorizontal,
    Pin,
    RefreshCw,
    Sparkles,
    Trash2,
    Upload,
    X,
} from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { BrandfetchDialog } from './BrandfetchDialog';
import { LogoSearchDialog } from './LogoSearchDialog';
import { BRAND_UPLOAD_ACCEPT, type BrandUpload, uploadToAsset, useBrandUploads } from './useBrandUploads';

const log = logger('BrandAssetsCard');

/** The slot a web-search result can be assigned to, with its human label. */
const SLOT_LABELS: Array<{ slot: BrandAssetSlot; label: string }> = [
    { slot: 'logo', label: 'Light-Mode Logo' },
    { slot: 'logoOnDark', label: 'Dark-Mode Logo' },
    { slot: 'icon', label: 'Icon' },
];

/** Where an asset's bytes came from, for the slot caption. Says nothing about who put it in the slot. */
const SOURCE_LABELS: Record<NonNullable<BrandLogoImage['source']>, string> = {
    brandfetch: 'Brandfetch',
    'web-search': 'Web search',
    upload: 'Uploaded',
};

interface BrandAssetsCardProps {
    brand: Brand;
    source: BrandSource | undefined;
    isEnriching: boolean;
    brandfetchCandidates: BrandfetchCandidate[];
    onBrandfetch: (domain?: string) => Promise<void>;
    onDismissCandidates: () => void;
    isSearchingLogos: boolean;
    logoOptions: BrandLogoImage[];
    onSearchLogos: (query: string) => Promise<void>;
    onAssignAsset: (slot: BrandAssetSlot, asset: BrandLogoImage) => Promise<void>;
    onClearAsset: (slot: BrandAssetSlot) => Promise<void>;
}

/**
 * The brand's asset curation card. Each slot previews on ITS OWN surface only — light-mode logo on
 * white, dark-mode logo on dark — mirroring exactly what the app renders in that theme, including the
 * cross-slot fallback (with a "Fallback" badge) when a slot is empty. Below, a collapsed "Other Logo
 * Options" browser re-picks from the stored snapshot: one wordmark per row, a light column that assigns
 * the light slot and a dark column that assigns the dark slot, with a check on the selected variant.
 */
export const BrandAssetsCard: FC<BrandAssetsCardProps> = ({
    brand,
    source,
    isEnriching,
    brandfetchCandidates,
    onBrandfetch,
    onDismissCandidates,
    isSearchingLogos,
    logoOptions,
    onSearchLogos,
    onAssignAsset,
    onClearAsset,
}) => {
    const [isAssigning, setIsAssigning] = useState(false);
    // Collapsed by default; auto-opened (this page load only) after a refetch so the fresh options show.
    const [optionsOpen, setOptionsOpen] = useState(false);
    // Every fetch goes through the dialog — see BrandfetchDialog for why a name search isn't one-click.
    const [fetchDialogOpen, setFetchDialogOpen] = useState(false);
    // Every web search goes through its dialog too — see LogoSearchDialog for the query composition.
    const [searchDialogOpen, setSearchDialogOpen] = useState(false);
    // Which slot a tapped web-search result fills — Trane's Brandfetch icon is junk, so an admin needs
    // to be able to point a web image at the icon slot (or either wordmark).
    const [webTarget, setWebTarget] = useState<BrandAssetSlot>('logo');
    // The uploads grid keeps its own target: the two sections are browsed independently, and sharing one
    // would silently re-aim whichever you looked at second.
    const [uploadTarget, setUploadTarget] = useState<BrandAssetSlot>('logo');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { uploads, isUploading, upload, remove } = useBrandUploads(brand.id);

    const handleBrandfetch = async (domain?: string) => {
        await onBrandfetch(domain);
        setOptionsOpen(true);
    };

    const handleAssignCandidate = async (candidate: BrandAssetCandidate, slot: BrandAssetSlot) => {
        if (!source || isAssigning) return;
        setIsAssigning(true);
        try {
            const asset = await candidateToAsset(candidate, slot, source);
            if (!asset) {
                toast.error('That asset can’t be sized (no dimensions or viewBox).');
                return;
            }
            await onAssignAsset(slot, asset);
        } finally {
            setIsAssigning(false);
        }
    };

    /** Assign a ready-made image (web-search result, upload) to any slot — including the icon. */
    const handleAssignOption = async (option: BrandLogoImage, slot: BrandAssetSlot) => {
        if (isAssigning) return;
        setIsAssigning(true);
        try {
            await onAssignAsset(slot, option);
        } finally {
            setIsAssigning(false);
        }
    };

    const handleUploadFile = async (file: File) => {
        try {
            await upload(file);
            toast.success('Image uploaded');
        } catch (err) {
            log.error('Failed to upload brand image', { err, brandId: brand.id });
            toast.error('Upload failed', {
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
            });
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDeleteUpload = async (target: BrandUpload) => {
        try {
            await remove(target);
            toast.success('Upload deleted');
        } catch (err) {
            log.error('Failed to delete brand upload', { err, path: target.path });
            toast.error('Unable to delete that upload');
        }
    };

    /** The slots currently showing this upload — an assigned upload can't be deleted out from under them. */
    const slotsUsing = (target: BrandUpload): BrandAssetSlot[] =>
        SLOT_LABELS.filter(({ slot }) => brand[slot]?.id === target.id).map(({ slot }) => slot);

    const wordmarkRows = source ? wordmarkCandidates(source) : [];
    const iconRows = source ? iconCandidates(source) : [];
    // A lone icon is auto-assigned by enrichment — only surface the picker when there's a real choice,
    // or when the sole candidate isn't assigned (e.g. an admin cleared the slot).
    const showIcons = iconRows.length > 1 || (iconRows.length === 1 && !brand.icon);

    return (
        <Card>
            <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <CardTitle>Logo & identity</CardTitle>
                        <CardDescription>
                            Brandfetch is the primary source; the web image search is a fallback for brands it doesn’t
                            know.
                        </CardDescription>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            size="sm"
                            onClick={() => setFetchDialogOpen(true)}
                            disabled={isEnriching || isSearchingLogos}
                        >
                            <Sparkles className={`mr-2 h-4 w-4 ${isEnriching ? 'animate-pulse' : ''}`} />
                            {isEnriching
                                ? 'Fetching…'
                                : brand.brandfetch
                                  ? 'Refetch from Brandfetch'
                                  : 'Fetch from Brandfetch'}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSearchDialogOpen(true)}
                            disabled={isEnriching || isSearchingLogos}
                        >
                            <RefreshCw className={`mr-2 h-4 w-4 ${isSearchingLogos ? 'animate-spin' : ''}`} />
                            {isSearchingLogos ? 'Searching…' : 'Search web images'}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploading}
                        >
                            {isUploading ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <Upload className="mr-2 h-4 w-4" />
                            )}
                            {isUploading ? 'Uploading…' : 'Upload an image'}
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
                <div className="grid gap-4 sm:grid-cols-3">
                    <WordmarkSlot
                        mode="light"
                        asset={brand.logo}
                        fallback={brand.logoOnDark}
                        onClear={() => void onClearAsset('logo')}
                    />
                    <WordmarkSlot
                        mode="dark"
                        asset={brand.logoOnDark}
                        fallback={brand.logo}
                        onClear={() => void onClearAsset('logoOnDark')}
                    />
                    <IconSlot asset={brand.icon} onClear={() => void onClearAsset('icon')} />
                </div>

                {brandfetchCandidates.length > 0 && (
                    <div>
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-foreground">
                                Which company is “{brand.canonicalName}”?
                            </h3>
                            <Button variant="ghost" size="icon" onClick={onDismissCandidates} aria-label="Dismiss">
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                        <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                            {brandfetchCandidates.map((candidate) => (
                                <li key={candidate.domain}>
                                    <button
                                        type="button"
                                        className="flex w-full items-center gap-3 rounded border bg-white p-2 text-left hover:border-primary disabled:opacity-50"
                                        onClick={() => void handleBrandfetch(candidate.domain)}
                                        disabled={isEnriching}
                                    >
                                        {candidate.iconUrl ? (
                                            <img
                                                src={candidate.iconUrl}
                                                alt=""
                                                className="h-10 w-10 rounded object-contain"
                                            />
                                        ) : (
                                            <span className="flex h-10 w-10 items-center justify-center rounded bg-muted">
                                                <Globe className="h-5 w-5 text-muted-foreground" />
                                            </span>
                                        )}
                                        <span className="min-w-0">
                                            <span className="block truncate text-sm font-medium">
                                                {candidate.name || candidate.domain}
                                            </span>
                                            <span className="block truncate text-xs text-muted-foreground">
                                                {candidate.domain}
                                            </span>
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {source && (wordmarkRows.length > 0 || showIcons) && (
                    <div>
                        <button
                            type="button"
                            className="flex items-center gap-2 text-sm font-semibold text-foreground"
                            onClick={() => setOptionsOpen((open) => !open)}
                            aria-expanded={optionsOpen}
                        >
                            <ChevronRight
                                className={`h-4 w-4 transition-transform ${optionsOpen ? 'rotate-90' : ''}`}
                            />
                            Other Logo Options
                            <span className="font-normal text-xs text-muted-foreground">
                                {source.domain} · {new Date(source.fetchedAt).toLocaleDateString()}
                            </span>
                        </button>
                        {optionsOpen && (
                            <div className="mt-3 flex flex-col gap-4">
                                {/* Two 50% columns: ALL options on light together, ALL on dark together —
                                    easier to compare within a surface than light/dark pairs per option. */}
                                {wordmarkRows.length > 0 && (
                                    <div className="grid grid-cols-2 gap-3">
                                        <PickerColumn label="Light Mode">
                                            {wordmarkRows.map((candidate) => (
                                                <SelectableTile
                                                    key={candidate.logoIndex}
                                                    url={previewUrl(candidate)}
                                                    caption={candidateLabel(candidate)}
                                                    surface="light"
                                                    selected={
                                                        brand.logo?.id ===
                                                        brandAssetId(source.domain, 'logo', candidate.logoIndex)
                                                    }
                                                    disabled={isAssigning}
                                                    onSelect={() => void handleAssignCandidate(candidate, 'logo')}
                                                />
                                            ))}
                                        </PickerColumn>
                                        <PickerColumn label="Dark Mode">
                                            {wordmarkRows.map((candidate) => (
                                                <SelectableTile
                                                    key={candidate.logoIndex}
                                                    url={previewUrl(candidate)}
                                                    caption={candidateLabel(candidate)}
                                                    surface="dark"
                                                    selected={
                                                        brand.logoOnDark?.id ===
                                                        brandAssetId(source.domain, 'logoOnDark', candidate.logoIndex)
                                                    }
                                                    disabled={isAssigning}
                                                    onSelect={() => void handleAssignCandidate(candidate, 'logoOnDark')}
                                                />
                                            ))}
                                        </PickerColumn>
                                    </div>
                                )}

                                {showIcons && (
                                    <div>
                                        <div className="pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                            Icons
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {iconRows.map((candidate) => (
                                                <SelectableTile
                                                    key={candidate.logoIndex}
                                                    url={previewUrl(candidate)}
                                                    caption={candidateLabel(candidate)}
                                                    surface="light"
                                                    selected={
                                                        brand.icon?.id ===
                                                        brandAssetId(source.domain, 'icon', candidate.logoIndex)
                                                    }
                                                    disabled={isAssigning}
                                                    onSelect={() => void handleAssignCandidate(candidate, 'icon')}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {uploads.length > 0 && (
                    <div>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <h3 className="text-sm font-semibold text-foreground">Uploaded Images</h3>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">Assign tap to</span>
                                <Select
                                    value={uploadTarget}
                                    onValueChange={(value) => setUploadTarget(value as BrandAssetSlot)}
                                >
                                    <SelectTrigger size="sm" className="w-[160px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SLOT_LABELS.map(({ slot, label }) => (
                                            <SelectItem key={slot} value={slot}>
                                                {label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="field-hint">
                            Kept until you delete them, so one upload can fill more than one slot. Assigning runs it
                            through the same sizing and padding pass as every other asset, so the tile below is the
                            source image, not the final one.
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {uploads.map((item) => {
                                const inUse = slotsUsing(item);
                                const asset = uploadToAsset(item);
                                return (
                                    <div key={item.id} className="relative">
                                        <CandidateTile
                                            option={asset}
                                            target={uploadTarget}
                                            selected={brand[uploadTarget]?.id === item.id}
                                            disabled={isAssigning}
                                            caption={item.fileName}
                                            onSelect={() => void handleAssignOption(asset, uploadTarget)}
                                        />
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="absolute left-1 top-1 h-6 w-6 bg-background/80"
                                            aria-label={`Delete ${item.fileName}`}
                                            disabled={inUse.length > 0}
                                            title={
                                                inUse.length > 0
                                                    ? `In use by the ${inUse
                                                          .map(
                                                              (slot) =>
                                                                  SLOT_LABELS.find((s) => s.slot === slot)?.label ??
                                                                  slot,
                                                          )
                                                          .join(' and ')} — clear the slot first`
                                                    : `Delete ${item.fileName}`
                                            }
                                            onClick={() => void handleDeleteUpload(item)}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {logoOptions.length > 0 && (
                    <div>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <h3 className="text-sm font-semibold text-foreground">Web Image Results</h3>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">Assign tap to</span>
                                <Select
                                    value={webTarget}
                                    onValueChange={(value) => setWebTarget(value as BrandAssetSlot)}
                                >
                                    <SelectTrigger size="sm" className="w-[160px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SLOT_LABELS.map(({ slot, label }) => (
                                            <SelectItem key={slot} value={slot}>
                                                {label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="field-hint">
                            Unvetted web images — verify it’s the right company’s mark before assigning. Tapping a
                            result assigns it to the {SLOT_LABELS.find((s) => s.slot === webTarget)?.label}.
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {logoOptions.map((option) => (
                                <CandidateTile
                                    key={option.id}
                                    option={option}
                                    target={webTarget}
                                    selected={brand[webTarget]?.id === option.id}
                                    disabled={isAssigning}
                                    onSelect={() => void handleAssignOption(option, webTarget)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>

            <input
                ref={fileInputRef}
                type="file"
                accept={BRAND_UPLOAD_ACCEPT}
                className="hidden"
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) void handleUploadFile(file);
                }}
            />

            <BrandfetchDialog
                brand={brand}
                open={fetchDialogOpen}
                isEnriching={isEnriching}
                onOpenChange={setFetchDialogOpen}
                onFetch={handleBrandfetch}
            />
            <LogoSearchDialog
                brand={brand}
                open={searchDialogOpen}
                isSearching={isSearchingLogos}
                onOpenChange={setSearchDialogOpen}
                onSearch={onSearchLogos}
            />
        </Card>
    );
};

/**
 * A wordmark slot previewed on its own surface only (light-mode logo on white, dark-mode on dark). When
 * the slot is empty but the opposite one is filled, previews the app's actual fallback behavior — the
 * opposite asset on this surface, plated when transparent — with a "Fallback" badge.
 */
const WordmarkSlot: FC<{
    mode: 'light' | 'dark';
    asset: BrandLogoImage | undefined;
    fallback: BrandLogoImage | undefined;
    onClear: () => void;
}> = ({ mode, asset, fallback, onClear }) => {
    const label = mode === 'light' ? 'Light-Mode Logo' : 'Dark-Mode Logo';
    const badge = mode === 'light' ? 'Light Mode' : 'Dark Mode';
    const shown = asset ?? fallback;
    const isFallback = !asset && !!fallback;

    return (
        <SlotFrame label={label} badge={badge} onClear={asset ? onClear : undefined}>
            {shown ? (
                <>
                    <SurfaceTile surface={mode} height={96}>
                        {isFallback && shown.transparent !== false ? (
                            // Mirror the app: a cross-mode fallback sits on a plate of the opposite color.
                            // Colors intentionally duplicate rock-app's config.app.brandLogo plate values
                            // (#ffffff / #141414) — admin can't import app config, so keep them in sync.
                            <span
                                // h-full so the img's height resolves against the tile — an
                                // unconstrained plate let the image render at natural size and get
                                // cropped to a horizontal slice by the tile's overflow-hidden.
                                // max-w-full resolves against the *tile*, which has a definite width;
                                // the plate itself shrink-wraps the artwork like the app's does.
                                className={`flex h-full max-w-full items-center justify-center rounded-lg p-2 ${
                                    mode === 'dark' ? 'bg-white' : 'bg-[#141414]'
                                }`}
                            >
                                {/* Sized off height with width from the aspect ratio, NOT FittedImage's
                                    max-w-full. Inside a shrink-to-fit plate a percentage max-width is
                                    cyclic (the plate's width depends on the image's). A raster resolves
                                    it from its intrinsic width, but an SVG saved with only a viewBox —
                                    no width/height attrs, which is how most vendor logos ship — has an
                                    aspect ratio and no intrinsic width, so Chrome resolves the used
                                    width to 0 and the plate collapses to a bare padding sliver. */}
                                <img src={shown.url} alt="" className="h-full w-auto object-contain" />
                            </span>
                        ) : (
                            <FittedImage asset={shown} />
                        )}
                    </SurfaceTile>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {isFallback ? (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800">
                                Fallback · using the {mode === 'light' ? 'dark' : 'light'}-mode logo
                            </span>
                        ) : (
                            <>
                                <span>
                                    {shown.width} × {shown.height}
                                    {shown.svgUrl ? ' · svg source' : ''}
                                </span>
                                <ProvenanceBadge asset={shown} />
                            </>
                        )}
                    </div>
                </>
            ) : (
                <EmptySlot hint={`No ${mode}-mode wordmark.`} />
            )}
        </SlotFrame>
    );
};

/** The square icon slot — icons bake their own background, so a light tile previews them faithfully. */
const IconSlot: FC<{ asset: BrandLogoImage | undefined; onClear: () => void }> = ({ asset, onClear }) => (
    <SlotFrame label="Icon" badge="Small Surfaces" onClear={asset ? onClear : undefined}>
        {asset ? (
            <>
                {/* Both surfaces: unlike the mode-specific wordmarks, the ONE icon serves light AND dark. */}
                <div className="grid grid-cols-2 gap-1">
                    <SurfaceTile surface="light" height={96}>
                        <img src={asset.url} alt="" className="max-h-full rounded-lg object-contain" />
                    </SurfaceTile>
                    <SurfaceTile surface="dark" height={96}>
                        <img src={asset.url} alt="" className="max-h-full rounded-lg object-contain" />
                    </SurfaceTile>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>
                        {asset.width} × {asset.height}
                    </span>
                    <ProvenanceBadge asset={asset} />
                </div>
            </>
        ) : (
            <EmptySlot hint="Square mark for badges and chips." />
        )}
    </SlotFrame>
);

/**
 * Where a filled slot's asset came from, and whether a human put it there — the two halves that used to
 * share one `source` field, where the authority stamp overwrote the origin on every admin pick.
 *
 * Provenance is shown only when the asset records it: assets assigned before the split parse as absent
 * rather than claiming an origin nobody recorded. The pin is what protects the slot from the next
 * Brandfetch refetch, so it's worth seeing at a glance.
 */
const ProvenanceBadge: FC<{ asset: BrandLogoImage }> = ({ asset }) => {
    const label = asset.source ? SOURCE_LABELS[asset.source] : undefined;
    if (!label && !asset.pinned) return null;
    return (
        <span className="flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
            {asset.pinned ? <Pin className="h-3 w-3" /> : null}
            {label ?? 'Pinned'}
        </span>
    );
};

/** Shared slot chrome: label + badge, the kebab menu hiding Clear, and the preview content. */
const SlotFrame: FC<{ label: string; badge: string; onClear: (() => void) | undefined; children: ReactNode }> = ({
    label,
    badge,
    onClear,
    children,
}) => (
    <div className="flex flex-col gap-2 rounded-lg border p-3">
        <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-foreground">{label}</span>
            <span className="flex items-center gap-1">
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                    {badge}
                </span>
                {onClear ? (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6" aria-label={`${label} actions`}>
                                <MoreHorizontal className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={onClear}>
                                <X className="mr-2 h-4 w-4" />
                                Clear
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                ) : null}
            </span>
        </div>
        {children}
    </div>
);

const EmptySlot: FC<{ hint: string }> = ({ hint }) => (
    <div className="flex h-24 flex-col items-center justify-center gap-1 rounded border border-dashed text-muted-foreground">
        <ImageOff className="h-5 w-5" />
        <span className="text-xs">{hint}</span>
    </div>
);

/** One 50% picker column: header + flex-wrapped option tiles, all previewed on the column's surface. */
const PickerColumn: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
    <div>
        <div className="pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="flex flex-wrap gap-2">{children}</div>
    </div>
);

/** A tap-to-assign option preview on one surface, with the selected check when it's the assigned one. */
const SelectableTile: FC<{
    url: string;
    caption: string;
    surface: 'light' | 'dark';
    selected: boolean;
    disabled: boolean;
    onSelect: () => void;
}> = ({ url, caption, surface, selected, disabled, onSelect }) => (
    <button
        type="button"
        className={`relative flex flex-col gap-1 rounded text-left ${
            selected ? 'ring-2 ring-primary' : 'hover:ring-1 hover:ring-primary/50'
        } disabled:opacity-50`}
        onClick={onSelect}
        disabled={disabled || selected}
        aria-pressed={selected}
        title={caption}
    >
        <SurfaceTile surface={surface} height={64}>
            <img src={url} alt="" className="max-h-full max-w-full object-contain" style={{ maxWidth: 160 }} />
        </SurfaceTile>
        <span className="max-w-[176px] truncate text-[10px] text-muted-foreground">{caption}</span>
        {selected ? (
            <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="h-3.5 w-3.5" />
            </span>
        ) : null}
    </button>
);

/**
 * A tap-to-assign candidate — a web-search result or an upload. Previews on the surface of the target
 * slot: a single tile for a wordmark (light/dark), or the white/black split for the icon slot (which
 * serves both themes), matching how an assigned icon previews above.
 *
 * Renders the ORIGINAL image, not the search engine's cached thumbnail: Google thumbs are flattened
 * JPEGs, so a transparent logo previews as a white box on the dark tile — the opposite of how the
 * assigned asset will actually render. Lazily loaded, since originals are full-resolution and there
 * can be dozens of results.
 */
const CandidateTile: FC<{
    option: BrandLogoImage;
    target: BrandAssetSlot;
    selected: boolean;
    disabled: boolean;
    /** Defaults to the dimensions; an upload shows its file name, which identifies it better. */
    caption?: string;
    onSelect: () => void;
}> = ({ option, target, selected, disabled, caption: captionOverride, onSelect }) => {
    const caption = captionOverride ?? `${option.width} × ${option.height}`;
    const img = (
        <img
            src={option.url}
            alt=""
            loading="lazy"
            decoding="async"
            className="max-h-full max-w-full object-contain"
            style={{ maxWidth: 160 }}
        />
    );
    return (
        <button
            type="button"
            className={`relative flex flex-col gap-1 rounded text-left ${
                selected ? 'ring-2 ring-primary' : 'hover:ring-1 hover:ring-primary/50'
            } disabled:opacity-50`}
            onClick={onSelect}
            disabled={disabled || selected}
            aria-pressed={selected}
            title={caption}
        >
            {target === 'icon' ? (
                <div className="grid grid-cols-2 gap-1">
                    <SurfaceTile surface="light" height={64}>
                        {img}
                    </SurfaceTile>
                    <SurfaceTile surface="dark" height={64}>
                        {img}
                    </SurfaceTile>
                </div>
            ) : (
                <SurfaceTile surface={target === 'logoOnDark' ? 'dark' : 'light'} height={64}>
                    {img}
                </SurfaceTile>
            )}
            <span className="max-w-[176px] truncate text-[10px] text-muted-foreground">{caption}</span>
            {selected ? (
                <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Check className="h-3.5 w-3.5" />
                </span>
            ) : null}
        </button>
    );
};

const SurfaceTile: FC<{ surface: 'light' | 'dark'; height: number; children: ReactNode }> = ({
    surface,
    height,
    children,
}) => (
    <span
        className={`flex items-center justify-center overflow-hidden rounded p-2 ${
            surface === 'light' ? 'border bg-white' : 'bg-[#141414]'
        }`}
        style={{ height }}
    >
        {children}
    </span>
);

const FittedImage: FC<{ asset: BrandLogoImage }> = ({ asset }) => (
    <img src={asset.url} alt="" className="max-h-full max-w-full object-contain" />
);

function candidateLabel(candidate: BrandAssetCandidate): string {
    const dims = candidate.width && candidate.height ? ` · ${candidate.width}×${candidate.height}` : '';
    return `${candidate.type} · ${candidate.theme ?? 'any'} · ${candidate.format}${dims}`;
}

/**
 * One row per wordmark ENTRY (dedupe the per-format flattening): prefer the raster format for preview
 * and assignment (it carries dimensions), falling back to the svg-only entries (sized on assign).
 */
function wordmarkCandidates(source: BrandSource): BrandAssetCandidate[] {
    return dedupeByEntry(
        brandAssetCandidates(source.payload).filter((c) => ['logo', 'other', 'symbol'].includes(c.type)),
    );
}

function iconCandidates(source: BrandSource): BrandAssetCandidate[] {
    return dedupeByEntry(brandAssetCandidates(source.payload).filter((c) => c.type === 'icon'));
}

function dedupeByEntry(candidates: BrandAssetCandidate[]): BrandAssetCandidate[] {
    const byEntry = new Map<number, BrandAssetCandidate>();
    for (const candidate of candidates) {
        const existing = byEntry.get(candidate.logoIndex);
        const hasDims = !!(candidate.width && candidate.height);
        const existingHasDims = !!(existing?.width && existing?.height);
        if (!existing || (hasDims && !existingHasDims)) {
            byEntry.set(candidate.logoIndex, candidate);
        }
    }
    return [...byEntry.values()].sort((a, b) => a.logoIndex - b.logoIndex);
}

/** Preview at a browser-friendly width — pure URL math, no API call (non-CDN URLs pass through). */
function previewUrl(candidate: BrandAssetCandidate): string {
    return candidate.format === 'svg' ? candidate.src : brandfetchRasterUrl(candidate.src, BRAND_LOGO_THUMB_WIDTH);
}

/**
 * Size a browsed candidate into an assignable `BrandLogoImage`. Rasters carry their own dimensions
 * (downsized via the CDN when above the slot's target, mirroring the server); an svg-only candidate is
 * fetched so its `viewBox` can size the CDN-rasterized PNG (same mechanism as the server's SVG rescue).
 */
async function candidateToAsset(
    candidate: BrandAssetCandidate,
    slot: BrandAssetSlot,
    source: BrandSource,
): Promise<BrandLogoImage | undefined> {
    const widths =
        slot === 'icon'
            ? { url: BRAND_ICON_URL_WIDTH, thumb: BRAND_ICON_THUMB_WIDTH }
            : { url: BRAND_LOGO_URL_WIDTH, thumb: BRAND_LOGO_THUMB_WIDTH };
    // The sibling svg format of the same logo entry, kept as vector metadata on the asset.
    const siblingSvg = brandAssetCandidates(source.payload).find(
        (c) => c.logoIndex === candidate.logoIndex && c.format === 'svg',
    );
    const base = {
        id: brandAssetId(source.domain, slot, candidate.logoIndex),
        transparent: candidate.transparent,
        ...(siblingSvg ? { svgUrl: siblingSvg.src } : {}),
    };

    if (candidate.format !== 'svg' && candidate.width && candidate.height) {
        // Downsize oversized rasters via the CDN, mirroring the server's pickToAsset — an admin pick is
        // stamped manual and rehosted permanently, so a native 2000px blob would otherwise stick around.
        const oversized = candidate.width > widths.url;
        return {
            ...base,
            url: oversized ? brandfetchRasterUrl(candidate.src, widths.url) : candidate.src,
            width: oversized ? widths.url : candidate.width,
            height: oversized ? Math.round((candidate.height * widths.url) / candidate.width) : candidate.height,
            thumbnail:
                candidate.width > widths.thumb ? brandfetchRasterUrl(candidate.src, widths.thumb) : candidate.src,
        };
    }

    // SVG path — also the rescue for a dimension-less raster whose entry ships a sibling svg (mirrors
    // the server's pickToAsset, which sizes such entries via the vector's viewBox).
    const svgSrc = candidate.format === 'svg' ? candidate.src : siblingSvg?.src;
    if (svgSrc) {
        try {
            const res = await fetch(svgSrc);
            const aspect = parseSvgAspect(await res.text());
            if (!aspect) return undefined;
            return {
                ...base,
                url: brandfetchRasterUrl(svgSrc, widths.url),
                width: widths.url,
                height: Math.round(widths.url / aspect),
                thumbnail: brandfetchRasterUrl(svgSrc, widths.thumb),
            };
        } catch (err) {
            log.error('Failed to fetch svg for sizing', { err, src: svgSrc });
            return undefined;
        }
    }

    return undefined;
}
